// Slide-up Ask-AI chat panel. History lives entirely in component state —
// the backend is stateless per-request, with the previous turns shipped
// back on every call so the model has conversation continuity.
//
// Phase 2 async flow: POST /ask-ai returns 202 { jobId }; we poll
// GET /ask-ai/jobs/:id every 1.5 s until done or failed.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Bot, Send, Loader2, AlertCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { usePostAskAi, customFetch } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { parseRateLimit } from "@/lib/rate-limit-toast";

type Turn = { role: "user" | "assistant"; content: string };

const MAX_HISTORY = 20;
const MAX_QUESTION_LEN = 2000;
const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 120;

interface AskAiJobStatusResponse {
  jobId: string;
  status: string;
  result: { answer?: string; model?: string; conversationId?: string } | null;
  errorMessage: string | null;
}

async function pollJobStatus(jobId: string): Promise<AskAiJobStatusResponse> {
  return customFetch<AskAiJobStatusResponse>(`/api/ask-ai/jobs/${jobId}`, {
    method: "GET",
    responseType: "json",
  });
}

export function AskAiPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [history, setHistory] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [, setNow] = useState(0);
  const [pollingJobId, setPollingJobId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollAttemptsRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const ask = usePostAskAi({
    mutation: {
      onSuccess: (data) => {
        if (data.jobId) {
          pollAttemptsRef.current = 0;
          setPollingJobId(data.jobId);
          setIsPolling(true);
          setError(null);
        }
      },
      onError: (err) => {
        const rl = parseRateLimit(err);
        if (rl) {
          setCooldownUntil(Date.now() + rl.retryAfterSec * 1000);
          setError(null);
          return;
        }
        const anyErr = err as { message?: string; response?: { data?: { error?: string } }; status?: number };
        const status = anyErr?.status;
        const msg =
          status === 503 ? "AI assistant isn't configured yet."
          : anyErr?.response?.data?.error ?? anyErr?.message ?? "Something went wrong";
        setError(msg);
      },
    },
  });

  // Tick once a second while a cooldown is active so the countdown re-renders.
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  // Phase 2 polling — keep fetching job status until done/failed or timeout.
  useEffect(() => {
    if (!pollingJobId || !isPolling) return;
    const currentJobId = pollingJobId;

    function scheduleNextPoll() {
      pollTimerRef.current = setTimeout(async () => {
        pollAttemptsRef.current += 1;
        if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
          setIsPolling(false);
          setPollingJobId(null);
          setError("The AI assistant took too long to respond. Please try again.");
          return;
        }
        try {
          const result = await pollJobStatus(currentJobId);
          if (result.status === "done") {
            const answer = result.result?.answer ?? "";
            if (answer) {
              setHistory((prev) =>
                [...prev, { role: "assistant" as const, content: answer }].slice(-MAX_HISTORY),
              );
            } else {
              setError("The AI returned an empty answer. Please try again.");
            }
            setIsPolling(false);
            setPollingJobId(null);
          } else if (result.status === "failed") {
            setError(result.errorMessage ?? "AI assistant failed. Please try again.");
            setIsPolling(false);
            setPollingJobId(null);
          } else {
            scheduleNextPoll();
          }
        } catch {
          scheduleNextPoll();
        }
      }, POLL_INTERVAL_MS);
    }

    scheduleNextPoll();
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingJobId, isPolling]);

  const cooldownSec = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
  const inCooldown = cooldownSec > 0;
  const isPending = ask.isPending || isPolling;

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [history, isPending]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const question = input.trim();
    if (!question || isPending || inCooldown) return;
    if (question.length > MAX_QUESTION_LEN) {
      setError(`Question is too long (${question.length} > ${MAX_QUESTION_LEN}).`);
      return;
    }
    setError(null);
    const nextHistory = [...history, { role: "user" as const, content: question }].slice(-MAX_HISTORY);
    setHistory(nextHistory);
    setInput("");
    ask.mutate({
      data: {
        question,
        history: nextHistory.slice(0, -1),
      },
    });
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Ask AI assistant"
      data-testid="ask-ai-panel"
      style={{ zIndex: 9998 }}
      className={cn(
        "fixed top-20 right-4 w-[min(420px,calc(100vw-2.5rem))] h-[min(560px,calc(100vh-7rem))]",
        "bg-background border rounded-xl shadow-2xl flex flex-col overflow-hidden",
      )}
    >
      <header className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-primary" />
          <div>
            <div className="font-semibold text-sm">Ask AI</div>
            <div className="text-[11px] text-muted-foreground">Internal assistant • uses live data</div>
          </div>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>Close</Button>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {history.length === 0 && !isPending ? (
          <div className="text-sm text-muted-foreground space-y-2">
            <p>Ask anything about your sales, inventory, or recent orders. Try:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>&quot;What were my top 3 products last week?&quot;</li>
              <li>&quot;How much GBP gold do I have in stock?&quot;</li>
              <li>&quot;Are there any orders awaiting shipping?&quot;</li>
            </ul>
          </div>
        ) : null}
        {history.map((m, i) => (
          <div
            key={i}
            className={cn(
              "max-w-[90%] rounded-lg px-3 py-2 text-sm",
              m.role === "user"
                ? "ml-auto bg-primary text-primary-foreground"
                : "mr-auto bg-muted",
            )}
          >
            {m.role === "assistant" ? (
              <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-pre:my-2">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
              </div>
            ) : (
              <span className="whitespace-pre-wrap">{m.content}</span>
            )}
          </div>
        ))}
        {isPending ? (
          <div className="mr-auto bg-muted rounded-lg px-3 py-2 text-sm flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
          </div>
        ) : null}
        {inCooldown ? (
          <div
            className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/30 rounded-md p-2"
            data-testid="ask-ai-cooldown"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>You&apos;ve asked a lot of questions — try again in {cooldownSec}s.</span>
          </div>
        ) : null}
        {error ? (
          <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 dark:bg-red-950/30 rounded-md p-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}
      </div>

      <form onSubmit={handleSubmit} className="border-t p-3 flex gap-2 items-end bg-background">
        <Textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={inCooldown ? `Try again in ${cooldownSec}s…` : "Ask about sales, stock, orders…"}
          rows={1}
          maxLength={MAX_QUESTION_LEN + 100}
          className="resize-none min-h-[40px] max-h-32 text-sm"
          data-testid="ask-ai-input"
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e as unknown as FormEvent);
            }
          }}
          disabled={isPending || inCooldown}
        />
        <Button type="submit" size="icon" disabled={isPending || inCooldown || !input.trim()} data-testid="ask-ai-send">
          {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
}
